import { useState, useRef, useEffect } from 'react'

/**
 * Custom Select with fully styled dropdown (no native blue highlight).
 * Options: [{ value, label }]. For form submit, use id/name and a hidden input is rendered.
 */
const Select = ({
  id,
  name,
  options = [],
  value,
  onChange,
  placeholder = 'Selecione...',
  required = false,
  compact = false,
  className = '',
  'aria-label': ariaLabel,
}) => {
  const [isOpen, setIsOpen] = useState(false)
  const [highlightedIndex, setHighlightedIndex] = useState(-1)
  const containerRef = useRef(null)

  const selectedOption = options.find((opt) => String(opt.value) === String(value))
  const displayLabel = selectedOption ? selectedOption.label : placeholder

  const close = () => {
    setIsOpen(false)
    setHighlightedIndex(-1)
  }

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) close()
    }
    const handleEscape = (e) => {
      if (e.key === 'Escape') close()
    }
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside)
      document.addEventListener('keydown', handleEscape)
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
      document.removeEventListener('keydown', handleEscape)
    }
  }, [isOpen])

  const handleKeyDown = (e) => {
    if (!isOpen) {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault()
        setIsOpen(true)
        setHighlightedIndex(options.findIndex((opt) => String(opt.value) === String(value)))
      }
      return
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      setHighlightedIndex((i) => (i < options.length - 1 ? i + 1 : 0))
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setHighlightedIndex((i) => (i > 0 ? i - 1 : options.length - 1))
    } else if (e.key === 'Enter' && highlightedIndex >= 0 && options[highlightedIndex]) {
      e.preventDefault()
      onChange(options[highlightedIndex].value)
      close()
    } else if (e.key === 'Escape') {
      close()
    }
  }

  const selectValue = (val) => {
    onChange(val)
    close()
  }

  return (
    <div ref={containerRef} className={`relative ${className}`}>
      {/* Hidden input for form submission */}
      {id && (
        <input
          type="hidden"
          id={id}
          name={name || id}
          value={value}
          required={required}
          readOnly
          tabIndex={-1}
          aria-hidden="true"
        />
      )}

      {/* Trigger button - looks like input field */}
      <button
        type="button"
        onClick={() => setIsOpen((o) => !o)}
        onKeyDown={handleKeyDown}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        aria-label={ariaLabel || placeholder}
        className={`input-field w-full text-left flex items-center justify-between gap-2 cursor-pointer ${
          compact ? '!min-h-[40px] !py-2 !px-3 !text-sm' : ''
        }`}
      >
        <span className={selectedOption ? 'text-gray-900 dark:text-white' : 'text-gray-400 dark:text-gray-400'}>
          {displayLabel}
        </span>
        <svg
          className={`w-5 h-5 text-gray-500 dark:text-gray-400 flex-shrink-0 transition-transform duration-200 ${isOpen ? 'rotate-180 text-accent' : ''}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
          aria-hidden="true"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {/* Custom dropdown list */}
      {isOpen && (
        <ul
          role="listbox"
          className="absolute z-50 left-0 right-0 mt-1 py-1 rounded-xl border-2 border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-800 shadow-lg overflow-hidden"
          style={{ minWidth: '100%' }}
        >
          {options.map((opt, index) => {
            const isSelected = String(opt.value) === String(value)
            const isHighlighted = index === highlightedIndex
            return (
              <li
                key={opt.value}
                role="option"
                aria-selected={isSelected}
                onMouseEnter={() => setHighlightedIndex(index)}
                onClick={() => selectValue(opt.value)}
                className={`
                  px-4 py-3 cursor-pointer transition-colors duration-150 text-base
                  ${isSelected ? 'bg-accent/20 text-accent dark:text-accent-light font-semibold' : 'text-gray-900 dark:text-white'}
                  ${isHighlighted && !isSelected ? 'bg-accent/10 dark:bg-accent/15 text-gray-900 dark:text-white' : ''}
                  hover:bg-accent/15 dark:hover:bg-accent/20
                `}
              >
                {opt.label}
              </li>
            )
          })}
        </ul>
      )}
    </div>
  )
}

export default Select
